import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  EcobookShare2,
  EcobookShare3,
  IncomeBanner,
  ListedBanner,
  RentedBanner,
  SpentBanner,
} from "../../assets/images";
import { RiExternalLinkLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { RentalType, VehicleType } from "../../types";

interface BannersProps {
  lesseeRentals: RentalType[];
  lessorRentals: RentalType[];
  vehicles: VehicleType[];
  type: string;
}

const Banners: FC = () => {
  const type = useSelector((state: any) => state.dashboardState.dashboardType);
  const lessorRentals: RentalType[] = useSelector(
    (state: any) => state.dashboardState.lessorRentals
  );
  const lesseeRentals: RentalType[] = useSelector(
    (state: any) => state.dashboardState.lesseeRentals
  );
  const vehicles: VehicleType[] = useSelector(
    (state: any) => state.dashboardState.vehicles
  );

  const images: string[] = [EcobookShare2, EcobookShare3];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [lessorIncome, setLessorIncome] = useState<{
    totalIncome: number;
    thisMonth: number;
    lastWeek: number;
  }>({
    totalIncome: 0,
    thisMonth: 0,
    lastWeek: 0,
  });

  const [lesseeSpent, setLesseeSpent] = useState<{
    totalSpent: number;
    thisMonth: number;
    lastWeek: number;
  }>({
    totalSpent: 0,
    thisMonth: 0,
    lastWeek: 0,
  });
  const [mostRentedVehicle, setMostRentedVehicle] =
    useState<VehicleType | null>(null);

  const [lesseeMostRentedVehicle, setLesseeMostRentedVehicle] = useState<{
    make: string;
    model: string;
    year: string;
  } | null>(null);
  const navigate = useNavigate();

  const handleEcobook = () => {
    navigate("/ecobook");
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000); // Change image every 1 minute
    // }, 60); // Change image every 1 minute

    // Cleanup interval on unmount
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (lessorRentals?.length) {
      const totalIncome = lessorRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .reduce((total, rental) => {
          return total + parseFloat(rental?.escrows?.depositAmount);
        }, 0);

      const thisMonth = lessorRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .filter((vehicle) => {
          const endDate = new Date(vehicle.rentalEndDatetime);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return endDate >= thirtyDaysAgo;
        })
        .reduce((total, vehicle) => {
          return total + parseFloat(vehicle?.escrows?.depositAmount);
        }, 0);

      const lastWeek = lessorRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .filter((vehicle) => {
          const endDate = new Date(vehicle.rentalEndDatetime);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          return endDate >= sevenDaysAgo;
        })
        .reduce((total, vehicle) => {
          return total + parseFloat(vehicle?.escrows?.depositAmount);
        }, 0);

      if (totalIncome && thisMonth && lastWeek) {
        setLessorIncome({
          totalIncome,
          thisMonth,
          lastWeek,
        });
      }
    }
    if (lesseeRentals?.length) {
      const totalSpent = lesseeRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .reduce((total, rental) => {
          return total + parseFloat(rental?.escrows?.depositAmount);
        }, 0);

      const thisMonth = lesseeRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .filter((vehicle) => {
          const endDate = new Date(vehicle.rentalEndDatetime);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return endDate >= thirtyDaysAgo;
        })
        .reduce((total, vehicle) => {
          return total + parseFloat(vehicle?.escrows?.depositAmount);
        }, 0);

      const lastWeek = lesseeRentals
        .filter((rental) => rental.rentalStatus === "Completed")
        .filter((vehicle) => {
          const endDate = new Date(vehicle.rentalEndDatetime);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          return endDate >= sevenDaysAgo;
        })
        .reduce((total, vehicle) => {
          return total + parseFloat(vehicle?.escrows?.depositAmount);
        }, 0);

      if (totalSpent && thisMonth && lastWeek) {
        setLesseeSpent({
          totalSpent,
          thisMonth,
          lastWeek,
        });
      }

      const vehicleRentalCounts = lesseeRentals.reduce((counts, rental) => {
        const carId = rental.car.id;
        counts[carId] = (counts[carId] || 0) + 1;
        return counts;
      }, {} as Record<string, number>);

      const maxRentals = Math.max(...Object.values(vehicleRentalCounts));

      const mostRentedVehicle = lesseeRentals
        .filter((rental) => vehicleRentalCounts[rental.car.id] === maxRentals)
        .map((rental) => rental.car);

      setLesseeMostRentedVehicle({
        make: mostRentedVehicle[0].make,
        model: mostRentedVehicle[0].model,
        year: mostRentedVehicle[0].year,
      });
    }

    if (vehicles?.length) {
      const vehicleWithMostRentals = vehicles.reduce((highest, vehicle) => {
        return vehicle.rentals.length > highest.rentals.length
          ? vehicle
          : highest;
      });
      setMostRentedVehicle(vehicleWithMostRentals);
    }
  }, [lessorRentals, lesseeRentals, vehicles]);

  return (
    <div className="flex flex-col lg:flex-row w-full gap-6 items-center max-h-fit">
      <div className=" w-full lg:w-[30%] h-full">
        {type !== "lessee" ? (
          <div className="flex w-full flex-col gap-6 items-center">
            <div className="relative bg-midGreen w-full h-[180px] rounded-lg ">
              <img
                src={IncomeBanner}
                alt=""
                className=" h-full w-full object-cover rounded-lg "
              />
              <div className=" absolute flex flex-col gap-3 p-3 top-0 bottom-0 my-auto h-full w-full ">
                <div className="flex w-full justify-between">
                  <p className=" font-Poppins text-[12px] text-white/80 leading-[145%]">
                    Income
                  </p>
                  <p className="text-white font-Poppins text-[12px] tracking-tight uppercase leading-[145%]">
                    Total
                  </p>
                </div>
                <div className="flex gap-1 flex-col">
                  <p className="text-white font-Poppins text-[40px] font-medium">
                    ${lessorIncome.totalIncome}
                    <span className=" text-white/80 text-[10px]">.00</span>
                  </p>
                  <p className="text-white font-Poppins text-[12px] font-normal">
                    This month's income:{" "}
                    <span className=" text-white/80 text-[15px]">
                      ${lessorIncome.thisMonth}
                    </span>
                  </p>
                  <p className="text-white font-Poppins text-[12px] font-normal">
                    Last week's Totals:{" "}
                    <span className=" text-white/80 text-[15px]">
                      ${lessorIncome.lastWeek}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="relative w-full bg-[#5D852A] h-[180px] rounded-lg">
              <img
                src={ListedBanner}
                alt=""
                className=" h-full w-full object-cover rounded-lg"
              />
              <div className="flex flex-col gap-3 p-3 absolute top-0 bottom-0 my-auto h-full w-full ">
                <div className="flex w-full justify-between">
                  <p className=" font-Poppins text-[12px] text-white/80 leading-[145%]">
                    Vehicle Listed
                  </p>
                  <p className="text-white font-Poppins text-[12px] tracking-tight uppercase leading-[145%]">
                    Total
                  </p>
                </div>
                <div className="flex gap-1 flex-col">
                  <p className="text-white font-Poppins text-[32px] font-medium">
                    {vehicles?.length ? vehicles.length : 0} Vehicles
                  </p>
                  <p className="text-white/80 font-Poppins text-[12px] font-normal">
                    Frequently Rented Vehicle:{" "}
                    <span className=" text-white text-[15px]">
                      {mostRentedVehicle?.make} {mostRentedVehicle?.model}{" "}
                      {mostRentedVehicle?.year}
                    </span>
                  </p>
                  {/* <p className="text-white/80 font-Poppins text-[12px] font-normal">
                    Peak Rental Times:{" "}
                    <span className=" text-white text-[15px]">
                      Mondays, 15:30
                    </span>
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 items-center">
            <div className="relative  bg-darkGreen w-full h-[180px] rounded-lg ">
              <img
                src={SpentBanner}
                alt=""
                className=" h-full w-full object-cover rounded-lg "
              />
              <div className="flex flex-col gap-3 p-3 absolute top-0 bottom-0 my-auto h-full w-full ">
                <div className="flex w-full justify-between">
                  <p className=" font-Poppins text-[12px] text-white/80 leading-[145%]">
                    Total Rented Spent
                  </p>
                  <p className="text-white font-Poppins text-[12px] tracking-tight uppercase leading-[145%]">
                    Total
                  </p>
                </div>
                <div className="flex gap-1 flex-col">
                  <p className="text-white font-Poppins text-[40px] font-medium">
                    ${lesseeSpent.totalSpent}
                    <span className=" text-white/80 text-[10px]">.00</span>
                  </p>
                  <p className="text-white font-Poppins text-[12px] font-normal">
                    Today's Expense:{" "}
                    <span className=" text-white/80 text-[15px]">
                      ${lesseeSpent.thisMonth}
                    </span>
                  </p>
                  <p className="text-white font-Poppins text-[12px] font-normal">
                    Last week's Totals:{" "}
                    <span className=" text-white/80 text-[15px]">
                      $ {lesseeSpent.lastWeek}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="relative  bg-[#5D852A] w-full h-[180px] rounded-lg">
              <img
                src={RentedBanner}
                alt=""
                className=" h-full w-full object-cover rounded-lg "
              />
              <div className="flex flex-col gap-3 p-3 absolute top-0 bottom-0 my-auto h-full w-full ">
                <div className="flex w-full justify-between">
                  <p className=" font-Poppins text-[12px] text-white/80 leading-[145%]">
                    Vehicles Rented
                  </p>
                  <p className="text-white font-Poppins text-[12px] tracking-tight uppercase leading-[145%]">
                    Total
                  </p>
                </div>
                <div className="flex gap-1 flex-col">
                  <p className="text-white font-Poppins text-[32px] font-medium">
                    {lesseeRentals?.length ? lesseeRentals.length : 0} Vehicles
                  </p>
                  <p className="text-white/80 font-Poppins text-[12px] font-normal">
                    Frequently Rented Vehicle:{" "}
                    <span className=" text-white text-[15px]">
                      {lesseeMostRentedVehicle?.make}{" "}
                      {lesseeMostRentedVehicle?.model}{" "}
                      {lesseeMostRentedVehicle?.year}
                    </span>
                  </p>
                  {/* <p className="text-white/80 font-Poppins text-[12px] font-normal">
                    Peak Rental Times:{" "}
                    <span className=" text-white text-[15px]">
                      Mondays, 15:30
                    </span>
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-1 relative w-full h-[366px]">
        <img
          src={images[currentImageIndex]}
          alt=""
          className="carousel-image object-cover w-full h-[366px] rounded-[10px]"
        />
        <div className="absolute h-full w-full px-6 flex flex-col items-center justify-center">
          <div className="flex flex-col gap-3 lg:gap-[27px] w-full ">
            <p className=" font-Poppins text-[14px] lg:text-[16px] font-medium text-white">
              Discover a new way to connect
            </p>
            <h1 className=" font-Poppins text-[20px] lg:text-[40px] font-medium text-white">
              <span className=" text-lightGreen font-semibold">Share</span>
              {""} on Ecobook
            </h1>
          </div>
          <div className=" flex w-full justify-between items-center">
            <div className="flex gap-2">
              {Array.from({ length: images.length }).map((_, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setCurrentImageIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full cursor-pointer ${
                    index === currentImageIndex
                      ? " bg-lightGreen"
                      : "bg-[#C4C4C4]"
                  }`}
                ></div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleEcobook}
              className="flex items-center gap-3 p-4 bg-white rounded-md text-midGreen text-[13px] font-semibold "
            >
              Go to Ecobook
              <RiExternalLinkLine />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banners;
